import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "./firebase";
import {
  Box,
  MenuItem,
  Select,
  FormControl,
  Typography,
  Divider,
} from "@mui/material";
import { People, Description, CheckCircle } from "@mui/icons-material";
import TaskAltIcon from "@mui/icons-material/TaskAlt";

const ClassDropdown = ({ user }) => {
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClass, setSelectedClass] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    fetchClassesAndEssays();
  }, [user?.uid]);

  const fetchClassesAndEssays = async () => {
    if (!user?.uid) return;

    try {
      // Hämta klasser
      const classesQuery = query(
        collection(db, "classes"),
        where("userId", "==", user.uid),
      );
      const classesSnapshot = await getDocs(classesQuery);
      const classesData = classesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
        essayCount: 0,
        reviewedCount: 0,
      }));

      // Hämta uppsatser och räkna granskade
      const classesWithEssays = await Promise.all(
        classesData.map(async (classItem) => {
          const essaysQuery = query(
            collection(db, "essays"),
            where("classId", "==", classItem.id),
          );
          const essaysSnapshot = await getDocs(essaysQuery);
          const essays = essaysSnapshot.docs;
          const reviewedEssays = essays.filter((doc) => doc.data().reviewed);

          return {
            ...classItem,
            essayCount: essays.length,
            reviewedCount: reviewedEssays.length,
          };
        }),
      );

      setClasses(classesWithEssays);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event) => {
    const classId = event.target.value;
    setSelectedClass(classId);
    navigate(`/app/klasser/${classId}`);
  };

  return (
    <FormControl fullWidth>
      <Select
        value={selectedClass}
        onChange={handleChange}
        displayEmpty
        sx={{ minWidth: 300 }}
      >
        <MenuItem value="" disabled>
          <Typography>Välj klass</Typography>
        </MenuItem>
        {classes.map((classItem) => (
          <MenuItem key={classItem.id} value={classItem.id}>
            <Box sx={{ width: "100%" }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "500", mb: 1 }}>
                {classItem.name}
              </Typography>
              <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <TaskAltIcon
                    sx={{ fontSize: 18, mr: 0.5, color: "#2E7D32" }}
                  />
                  <Typography variant="body2">
                    {classItem.reviewedCount}/{classItem.essayCount} granskade
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <People
                    sx={{ fontSize: 18, mr: 0.5, color: "text.secondary" }}
                  />
                  <Typography variant="body2">
                    {classItem.studentCount} elever
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Description
                    sx={{ fontSize: 18, mr: 0.5, color: "text.secondary" }}
                  />
                  <Typography variant="body2">
                    {classItem.essayCount} bedömningar
                  </Typography>
                </Box>
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default ClassDropdown;
